import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  StackProps,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { useFormik } from "formik";
import { lazy, Suspense, useState } from "react";
import * as yup from "yup";
import { ShippingInterface } from "../../constants/SupplierProfile";
import useCustomToast from "../../lib/useCustomToast";
import { axiosSupplier } from "../../utils/AxiosInstance";
import { explodeString } from "../../utils/helperFunction";
import { NumberInput } from "../input/NumberInput";
import { CardSkeleton } from "../skeleton/CardSkeleton";

const SelectPublicApi = lazy(() => import("../input/SelectPublicApi"));

interface Props extends StackProps {
  data: ShippingInterface | undefined;
  loaded: boolean;
}

export const TabSupplierShippingAddress = ({
  data,
  loaded,
  ...rest
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useCustomToast();

  const initialValues = {
    name: data && data.name,
    village: data && data.kelurahan,
    subdistrict: data && data.kecamatan,
    city: data && data.kota,
    province: data && data.provinsi,
    postalCode: data && data.kodepos,
    address: data && data.address,
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: yup.object().shape({
      name: yup.string().required("Nama pengirim harus diisi"),
      village: yup.string().required("Kelurahan harus diisi"),
      subdistrict: yup.string().required("Kecamatan harus diisi"),
      city: yup.string().required("Kabupate/Kota harus diisi"),
      province: yup.string().required("Provinsi harus diisi"),
      postalCode: yup
        .string()
        .required("Kode POS harus diisi")
        .max(8)
        .typeError("Maksimal 8 angka"),
      address: yup.string().required("Alamat harus diisi"),
    }),
    onSubmit: async (values) => {
      setLoading(true);

      const request = {
        name: values.name,
        address: values.address,
        province: values.province,
        city: values.city,
        kecamatan: values.subdistrict,
        kelurahan: values.village,
        kodepos: values.postalCode,
      };

      axiosSupplier
        .put(`/v1/supplier/profile-shipping`, request)
        .then((res) => {
          toast({
            title: res.data.message,
            status: "success",
          });
        })
        .catch((err) => {
          toast({
            description: err?.response?.data?.message || "",
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
  });

  return (
    <form
      id="supplierShippingAddress"
      onSubmit={formik.handleSubmit}
      style={{ width: "100%" }}
    >
      <VStack w={"100%"} gap={6} align={"stretch"} {...rest}>
        <FormControl
          isInvalid={formik.errors.name && formik.touched.name ? true : false}
        >
          <FormLabel htmlFor="name">Nama Pengirim</FormLabel>
          {!loaded ? (
            <CardSkeleton row={1} height={40} />
          ) : (
            <Input
              name="name"
              type="text"
              onChange={formik.handleChange}
              value={formik.values.name || ""}
            />
          )}
          <FormErrorMessage>{formik.errors.name}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={
            formik.errors.province && formik.touched.province ? true : false
          }
        >
          <FormLabel htmlFor="province">Provinsi</FormLabel>
          <Suspense fallback={<CardSkeleton row={1} height={40} />}>
            <SelectPublicApi
            level="province"
              endpoint="/v1/masterdata/rajaongkir/province"
              inputValue={formik.values.province}
              mustFilled={false}
              onSelected={(id) => {
                formik.setFieldValue("province", id);
              }}
            />
          </Suspense>
          <FormErrorMessage>{formik.errors.province}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={formik.errors.city && formik.touched.city ? true : false}
        >
          <FormLabel htmlFor="city">Kabupaten/Kota</FormLabel>
          <Suspense fallback={<CardSkeleton row={1} height={40} />}>
            <SelectPublicApi
              level="city"
              endpoint={`/v1/masterdata/rajaongkir/city/${
                explodeString(formik.values.province).id
              }`}
              inputValue={formik.values.city}
              mustFilled={true}
              mustFilledData={formik.values.province}
              placeholder="Pilih Kota/Kabupaten"
              onSelected={(id) => {
                formik.setFieldValue("city", id);
              }}
            />
          </Suspense>
          <FormErrorMessage>{formik.errors.city}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={
            formik.errors.subdistrict && formik.touched.subdistrict
              ? true
              : false
          }
        >
          <FormLabel htmlFor="subdistrict">Kecamatan</FormLabel>
          <Suspense fallback={<CardSkeleton row={1} height={40} />}>
            <SelectPublicApi
              level="district"
              endpoint={`/v1/masterdata/rajaongkir/subdistrict/${
                explodeString(formik.values.city).id
              }`}
              inputValue={formik.values.subdistrict}
              mustFilled={true}
              mustFilledData={formik.values.city}
              placeholder="Pilih Kecamatan"
              onSelected={(id) => {
                formik.setFieldValue("subdistrict", id);
              }}
            />
          </Suspense>
          <FormErrorMessage>{formik.errors.subdistrict}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={
            formik.errors.village && formik.touched.village ? true : false
          }
        >
          <FormLabel htmlFor="village">Desa</FormLabel>
          <Suspense fallback={<CardSkeleton row={1} height={40} />}>
            {!loaded ? (
              <CardSkeleton row={1} height={40} />
            ) : (
              <Input
                name="village"
                type="text"
                autoComplete="off"
                onChange={formik.handleChange}
                value={formik.values.village || ""}
              />
            )}
          </Suspense>
          <FormErrorMessage>{formik.errors.village}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={
            formik.errors.postalCode && formik.touched.postalCode ? true : false
          }
        >
          <FormLabel htmlFor="postalCode">Kode Pos</FormLabel>
          {!loaded ? (
            <CardSkeleton row={1} height={40} />
          ) : (
            <NumberInput
              name="postalCode"
              inputValue={formik.values.postalCode}
              isCurrency={false}
              maxLength={5}
              onConfirm={(input) => {
                formik.setFieldValue("postalCode", input);
              }}
            />
          )}
          <FormErrorMessage>{formik.errors.postalCode}</FormErrorMessage>
        </FormControl>

        <FormControl
          isInvalid={
            formik.errors.address && formik.touched.address ? true : false
          }
        >
          <FormLabel htmlFor="address">Alamat</FormLabel>
          {!loaded ? (
            <CardSkeleton row={1} height={40} />
          ) : (
            <Textarea
              name="address"
              onChange={formik.handleChange}
              value={formik.values.address || ""}
            />
          )}
          <FormErrorMessage>{formik.errors.address}</FormErrorMessage>
        </FormControl>

        <Button
          form="supplierShippingAddress"
          w={"fit-content"}
          colorScheme="red"
          type="submit"
          isLoading={loading}
          isDisabled={loaded ? false : true}
          loadingText={"Loading"}
        >
          Tetapkan Alamat Pengiriman
        </Button>
      </VStack>
    </form>
  );
};
